import {FormInstance} from 'antd/es/form';
import {MutableRefObject} from 'react';

import {IModalOpenerComponentRef} from 'components/modal-opener-component';
import {TStateSetter} from 'shared/types/generics';

import {ISendSignedFileArgs} from '../api/types';
import {
    getFilesToSign, processDocuments, processSignatures, validateCertificate,
} from '../lib';
import {EFileEncoding} from '../model/constants';
import {
    TCertificateExtended, TCryptoProSignatureType, IDocumentToSignInDatabase, IFileWithId,
} from '../model/types';

interface IBaseSigningParams {
    entityName: string;
    selectedCertificate?: TCertificateExtended;
    selectedSignatureType: TCryptoProSignatureType;
    isWithResign: boolean;
    modalRef: MutableRefObject<IModalOpenerComponentRef | null>;
    selectedFileExtension?: string;
    selectedFileEncoding?: EFileEncoding;
}

interface IFileSigningParams extends IBaseSigningParams {
    files?: IFileWithId[];
    fileFieldNames?: string[];
    fileList?: File[];
    form?: FormInstance;
    documentsToSignInDB?: IDocumentToSignInDatabase[];
    value?: IFileWithId[];
    setIsSigning: TStateSetter<boolean>;
    onChange?: (files: File[], newValue: any) => void;
}

interface IDocumentSigningParams extends IBaseSigningParams {
    documentIdsToSign?: string[];
    attachmentIdsToSign?: string[];
    onStart?: () => void;
    onFinish?: () => void;
    handleSignedFileUpload?: (args: ISendSignedFileArgs) => void;
  }

/**
 * Хук используется для обработки подписания файлов или документов
 * с использованием выбранного сертификата и алгоритма подписи при помощи плагина CryptoPro.
 * В зависимости от переданных параметров (файлы или документы) хук вызывает соответствующие функции для подписи,
 * такие как `processDocuments` или `processSignatures`, также выполняя валидацию сертификата
 * и организует выполнение процесса подписания с уведомлениями о ходе его выполнения и возникающих ошибках.
 *
 * @param params Параметры, которые могут включать файлы или документы для подписания,
 * а также информацию о сертификате и алгоритме подписи.
 *
 * @returns Объект с функцией `handleFinish`,
 * которая запускает процесс подписания в зависимости от переданных параметров.
 */
export const useCryptoProSigning = (
    params: IFileSigningParams | IDocumentSigningParams,
) => {
    const handleFinish = () => {
        const {
            selectedCertificate,
            selectedSignatureType,
            selectedFileExtension,
            selectedFileEncoding,
            isWithResign,
            entityName,
            modalRef,
        } = params;
        const validatedCertificate = validateCertificate(selectedCertificate);
        if (!validatedCertificate || !selectedCertificate) return;

        const {thumbprint, algorithm} = validatedCertificate;

        if ('files' in params || 'fileList' in params) {
            const {
                files,
                form,
                fileFieldNames,
                onChange,
                documentsToSignInDB,
                value,
                setIsSigning,
                fileList,
            } = params;

            const {filesToSign, overlappingFileNamesInDB} = getFilesToSign({
                form,
                fileFieldNames,
                inputFileValue: files,
                isWithResign,
                fileList,
            });

            modalRef.current?.closeModal();

            processSignatures({
                filesToSign,
                entityName,
                documentsToSignInDB,
                certificateThumbprint: thumbprint,
                certificateCadescomAlgorithm: algorithm,
                isWithResign,
                overlappingFileNamesInDB,
                value,
                setIsSigning,
                selectedCertificate,
                selectedSignatureType,
                selectedFileExtension,
                selectedFileEncoding,
                onChange,
                modalRef,
            });
        } else if ('documentIdsToSign' in params || 'attachmentIdsToSign' in params) {
            const {
                documentIdsToSign,
                attachmentIdsToSign,
                onStart,
                onFinish,
                handleSignedFileUpload,
            } = params;

            processDocuments({
                documentIdsToSign,
                attachmentIdsToSign,
                entityName,
                certificateThumbprint: thumbprint,
                certificateCadescomAlgorithm: algorithm,
                selectedCertificate,
                selectedSignatureType,
                selectedFileExtension,
                selectedFileEncoding,
                modalRef,
                handleSignedFileUpload,
                onStart,
                onFinish,
            });
        }
    };

    return {
        handleFinish,
    };
};
